import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ToolTypes, ToolBaseTypes, Sites } from '@labradorsports/constants';
import {
    applyFormationInitial,
    applyOffset,
    conformPlayData,
    createDefaultGoals,
    ensureFootballPlayDefaults,
    Field,
    getEmptyFrame,
    getFormationAnchor,
    getLinePlayerPosition,
    getNewPlay,
    PlayData,
    removeBallMovement,
    removePlayerFromFrames,
    updateExistingOrCreateNew,
} from '@labradorsports/play-rendering';

import { generatePlayConceptId } from '../../play-editor/piece-editors/editor-panel-utils.js';
import { linePlayerId } from '../../shared/utils.js';
import { authActions } from '../auth/index.js';

export interface PlayEditorState {
    PlayConfig: PlayConfigSpec;
    playModified: boolean;
    currentFrame: number;
    savingPlay: boolean;

    play: PlayData;
    debugOptions: any;
    debugMarkers: any[];

    editHistory: any[];
}

const playEditorDefaultState: PlayEditorState = {
    PlayConfig: undefined,
    play: undefined,
    currentFrame: 0,
    playModified: false,
    savingPlay: false,
    debugOptions: null,
    debugMarkers: [],
    editHistory: [],
};

function removeConceptsForPlayerIds(play: PlayData, removedPlayerIds: number[]) {
    if (!removedPlayerIds.length || !play.concepts?.length) {
        return;
    }

    const removedSet = new Set(removedPlayerIds);
    const removedPlayConceptIds = new Set(
        play.concepts
            .filter((concept) => concept.playerIds.some((id) => removedSet.has(id)))
            .map((concept) => concept.id)
    );

    if (removedPlayConceptIds.size === 0) {
        return;
    }

    play.concepts = play.concepts.filter((concept) => !removedPlayConceptIds.has(concept.id));

    if (play.viewOptions?.activeConceptId && removedPlayConceptIds.has(play.viewOptions.activeConceptId)) {
        play.viewOptions = {
            ...play.viewOptions,
            activeConceptId: null,
            spotlightPlayerIds: (play.viewOptions.spotlightPlayerIds ?? []).filter((id) => !removedSet.has(id)),
        };
    }
}

function tagPlayWithFormation(
    play: PlayData,
    { id, name, attributes = [] }: { id?: string | null; name: string; attributes?: string[] }
) {
    play.tags = [...(play.tags ?? []), name, ...attributes];

    // Save formation tags so we can hide them in UI later
    play.formationTags = attributes;
    play.formationId = id ?? null;
}

const playModifierReducers = {
    AddFrame: (state, action: PayloadAction<number | null>) => {
        if (action.payload !== undefined) {
            state.play.frames = [
                ...state.play.frames.slice(0, action.payload + 1),
                getEmptyFrame(),
                ...state.play.frames.slice(action.payload + 1),
            ];
            state.currentFrame = action.payload + 1;
        } else {
            state.play.frames.push(getEmptyFrame());
            state.currentFrame = state.play.frames.length - 1;
        }
    },

    UpdateMove: (state, action: PayloadAction<any>) => {
        const { frameIdx = Math.floor(state.currentFrame), ...move } = action.payload;
        const frame = state.play.frames[frameIdx];

        const existingMove = frame.moves.find((mv: any) => {
            return mv.target === action.payload.target;
        });

        const oldType = existingMove?.type;

        if (existingMove) {
            Object.assign(existingMove, move);
        } else {
            frame.moves.push(move);
        }

        if (move.type === ToolTypes.ZONE) {
            state.play.fieldPieces = state.play.fieldPieces ?? [];

            const lastAnchor = move.anchors[move.anchors.length - 1];
            const existingShading = state.play.fieldPieces.find(({ moveId }) => moveId === move.target);

            updateExistingOrCreateNew(state.play.fieldPieces, {
                type: ToolTypes.ELLIPSE,
                moveId: move.target,
                width: 10,
                height: 10,
                frameIdx,
                ...existingShading,
                origin: lastAnchor,
            });
        } else if (oldType === ToolTypes.ZONE) {
            state.play.fieldPieces = state.play.fieldPieces.filter((piece) => piece.moveId !== move.target);
        }
    },

    UpdatePass: (state, action: PayloadAction<any>) => {
        const { PlayTypes } = state.PlayConfig;
        const { frameIdx = Math.floor(state.currentFrame), ...pass } = action.payload;
        const currentFrame = state.play.frames[frameIdx];

        if (state.play.type === PlayTypes.DRILL) {
            currentFrame.passes = currentFrame.passes ?? [];

            updateExistingOrCreateNew(currentFrame.passes, {
                ...pass,
                // Pass ID should match ball ID to keep a single pass per ball per frame
                id: pass.id ?? pass.ball,
            });
        } else {
            currentFrame.passes = [
                {
                    // Copy existing pass props
                    ...(currentFrame.passes?.[0] ?? {}),
                    ...pass,
                    id: 0,
                },
            ];
        }

        currentFrame.shots = currentFrame.shots?.filter((shot: any) => shot.ball !== action.payload.ball) ?? [];
    },

    UpdateOption: (state, action: PayloadAction<any>) => {
        const { frameIdx = Math.floor(state.currentFrame), ...option } = action.payload;
        const currentFrame = state.play.frames[frameIdx];

        if (!currentFrame.options) currentFrame.options = [];

        updateExistingOrCreateNew(currentFrame.options, {
            ...option,
            type: ToolTypes.OPTION,
        });
    },

    UpdateRouteOption: (state, action: PayloadAction<any>) => {
        const { frameIdx = Math.floor(state.currentFrame), ...routeOption } = action.payload;
        const currentFrame = state.play.frames[frameIdx];

        if (!currentFrame.routeOptions) currentFrame.routeOptions = [];

        updateExistingOrCreateNew(currentFrame.routeOptions, {
            ...routeOption,
            type: ToolTypes.ROUTE_OPTION,
        });
    },

    UpdatePlayer: (state, action: PayloadAction<any>) => {
        updateExistingOrCreateNew(state.play.initial, action.payload);
    },

    DeletePlayer: (state, action: PayloadAction<any>) => {
        if (typeof action.payload === 'undefined') {
            return;
        }

        const players = state.play.initial;
        const [removed] = players.splice(
            players.findIndex((plr) => plr.id === action.payload),
            1
        );

        const ballIdx = state.play.balls.findIndex((ball) => ball.carrier === action.payload);

        if (ballIdx !== -1) {
            state.play.balls.splice(ballIdx, 1);
        }

        if (typeof removed.pieceId === 'number') {
            const piece = state.play.fieldPieces.find((pc) => pc.id === removed.pieceId);

            if (piece?.type === ToolTypes.GOAL) {
                piece.goalieVisible = false;
            }
        }

        removePlayerFromFrames(state.play.frames, removed);
        removeConceptsForPlayerIds(state.play, [action.payload]);
    },

    UpdateFrameRole: {
        prepare: (playerId: number, role?: string): any => ({
            payload: {
                id: playerId,
                role,
            },
        }),
        reducer: (state, action: PayloadAction<any>): void => {
            // Frame roles are stored in frame.slides
            const currentFrame = state.play.frames[state.currentFrame];

            // Initialize slides if it doesn't exist yet
            currentFrame.slides = currentFrame.slides ?? [];

            const { slides } = currentFrame;

            const existingIdx = slides.findIndex((sl: any) => sl.target === action.payload.id);

            if (action.payload.role) {
                const slide = {
                    target: action.payload.id,
                    role: action.payload.role,
                };

                if (existingIdx !== -1) {
                    slides.splice(existingIdx, 1, slide);
                } else {
                    slides.push(slide);
                }
            } else if (existingIdx !== -1) {
                slides.splice(existingIdx, 1);
            }
        },
    },

    UpdateAssignment: {
        prepare: (id: number, defId: number, atkId?: number): any => ({
            payload: {
                id,
                defId,
                atkId,
            },
        }),
        reducer: (state, action: PayloadAction<any>): void => {
            const currentFrame = state.play.frames[state.currentFrame];

            currentFrame.assignments = currentFrame.assignments ?? [];

            const { assignments } = currentFrame;

            const existingIdx = assignments.findIndex((asn: any) => asn.id === action.payload.id);
            if (existingIdx !== -1) {
                // Is there another assignment with the same two targets?
                const sameIdx = assignments.findIndex(
                    (asn: any) =>
                        asn.defId === action.payload.defId &&
                        asn.atkId === action.payload.atkId &&
                        asn.id !== action.payload.id
                );

                if (sameIdx === -1) {
                    assignments.splice(existingIdx, 1, action.payload);
                } else {
                    // If there is, remove this one
                    assignments.splice(existingIdx, 1);
                }
            } else {
                let newId = 0;
                const findExisting = (asn: any) => asn.id === newId;
                while (assignments.find(findExisting)) {
                    newId += 1;
                }

                const newAsn = {
                    ...action.payload,
                    id: newId,
                };

                if (newAsn.atkId === undefined) {
                    delete newAsn.atkId;
                }

                assignments.push(newAsn);
            }
        },
    },

    RemoveMove: (state, action: PayloadAction<any>) => {
        const { MoveConfig } = state.PlayConfig;
        const { frameIdx: explicitFrameIdx, ...payload } = action.payload;
        const frameIdx = typeof explicitFrameIdx === 'number' ? explicitFrameIdx : Math.floor(state.currentFrame);
        const trgPlayer = state.play.initial.find((plr) => plr.id === payload.target);
        const trgPlayerPiece = trgPlayer?.pieceId && state.play.fieldPieces.find((pc) => pc.id === trgPlayer.pieceId);

        const frame = state.play.frames[frameIdx];

        if (
            frame.shots?.length > 0 &&
            (MoveConfig[payload.type]?.baseType === ToolTypes.SHOOT || trgPlayerPiece?.type === ToolTypes.GOAL)
        ) {
            frame.shots = frame.shots.filter((shot: any) => shot.id !== payload.id);
        } else if (MoveConfig[payload.type]?.baseType === ToolBaseTypes.PASS) {
            frame.passes = frame.passes.filter((pass: any) => pass.id !== payload.id);
        } else if (payload.type === ToolTypes.ASSIGN) {
            frame.assignments = frame.assignments.filter((asn: any) => asn.id !== payload.id);
        } else if (payload.type === ToolTypes.OPTION) {
            frame.options = frame.options.filter((opt: any) => opt.id !== payload.id);
        } else if (payload.type === ToolTypes.ROUTE_OPTION) {
            frame.routeOptions = frame.routeOptions.filter((ro: any) => ro.id !== payload.id);
        } else if (payload.type === ToolTypes.COMMENT) {
            frame.fieldComments = frame.fieldComments.filter((comment: any) => comment.id !== payload.id);
        } else {
            frame.moves = frame.moves.filter((mv: any) => mv.target !== payload.target);

            if (payload.type === ToolTypes.ZONE) {
                state.play.fieldPieces = state.play.fieldPieces.filter((piece) => piece.moveId !== payload.target);
            }
        }
    },

    UpdateShot: (state, action: PayloadAction<any>) => {
        const { PlayTypes } = state.PlayConfig;
        const currentFrame = state.play.frames[state.currentFrame];
        if (state.play.type === PlayTypes.DRILL) {
            currentFrame.shots = currentFrame.shots ?? [];
            updateExistingOrCreateNew(currentFrame.shots, {
                // Shot ID should match ball ID to keep a single pass per ball per frame
                ...action.payload,
                type: ToolTypes.SHOOT,
                id: action.payload.id ?? action.payload.ball,
            });
        } else {
            currentFrame.shots = [
                {
                    // Copy existing shot props
                    type: ToolTypes.SHOOT,
                    ...(currentFrame.shots?.[0] ?? {}),
                    ...action.payload,
                    id: 0,
                },
            ];
        }

        currentFrame.passes = currentFrame.passes?.filter((pass: any) => pass.ball !== action.payload.ball) ?? [];
    },

    UpdateFieldComment: (state, action: PayloadAction<any>) => {
        const currentFrame = state.play.frames[state.currentFrame];

        // Initialize fieldComments if it doesn't exist yet
        currentFrame.fieldComments = currentFrame.fieldComments ?? [];

        updateExistingOrCreateNew(currentFrame.fieldComments, action.payload);
    },

    UpdateFieldPiece: (state, action: PayloadAction<any>) => {
        const { FieldTypes, FieldSettings } = state.PlayConfig;
        // Initialize fieldPieces if it doesn't exist yet
        state.play.fieldPieces = state.play.fieldPieces ?? [];

        const { fieldPieces } = state.play;
        const piece = action.payload;

        updateExistingOrCreateNew(fieldPieces, piece);

        const newPiece =
            typeof piece.id !== 'undefined'
                ? fieldPieces.find((pc) => pc.id === piece.id)
                : fieldPieces[fieldPieces.length - 1];

        if ([ToolTypes.OLINE, ToolTypes.DLINE].includes(newPiece.type)) {
            const minPlayers = 2;
            const maxPlayers = 8;

            newPiece.playerCount = Math.min(
                maxPlayers,
                Math.max(minPlayers, newPiece.playerCount === undefined ? 3 : newPiece.playerCount)
            );

            const { width, height, padding, sideline } =
                state.play.fieldType === FieldTypes.CUSTOM ? state.play.fieldConfig : FieldSettings[state.play.sport];

            const linePlayers = new Array(newPiece.playerCount).fill(null).map((_, i) => {
                const position = getLinePlayerPosition(
                    newPiece.origin,
                    newPiece.playerCount,
                    i,
                    new Field(width, height, padding, sideline),
                    newPiece.rotation
                );

                const existing = state.play.initial.find(
                    (plr) => plr.pieceId === newPiece.id && plr.linePosition === i
                );

                if (existing) {
                    return {
                        ...existing,
                        origin: position,
                    };
                }

                return {
                    id: linePlayerId(newPiece, i),
                    pieceId: newPiece.id,
                    type: newPiece.type === ToolTypes.OLINE ? ToolTypes.BLUEPLAYER : ToolTypes.ORANGEPLAYER,
                    origin: position,
                    linePosition: i,
                };
            });

            state.play.initial = state.play.initial.filter((plr) => plr.pieceId !== newPiece.id).concat(linePlayers);
        } else if (newPiece.type === ToolTypes.GOAL) {
            newPiece.goalieType = newPiece.goalieType ?? ToolTypes.ORANGEPLAYER;

            state.play.initial = state.play.initial.filter((plr) => plr.pieceId !== newPiece.id);

            updateExistingOrCreateNew(state.play.initial, {
                type: newPiece.goalieType,
                pieceId: newPiece.id,
                origin: newPiece.origin,
            });
        } else if (
            [ToolTypes.ELLIPSE, ToolTypes.RECTANGLE].includes(newPiece.type) &&
            typeof newPiece.moveId !== 'undefined'
        ) {
            const currentFrame = state.play.frames[newPiece.frameIdx ?? state.currentFrame];
            const move = currentFrame.moves.find(({ target }) => newPiece.moveId === target);
            newPiece.origin = move.anchors[move.anchors.length - 1];
        }
    },

    RemoveFieldPiece: (state, action: PayloadAction<any>) => {
        const { fieldPieces } = state.play;
        const [removed] = fieldPieces.splice(
            fieldPieces.findIndex((pc) => pc.id === action.payload),
            1
        );

        if (removed.type === ToolTypes.OLINE || removed.type === ToolTypes.DLINE || removed.type === ToolTypes.GOAL) {
            const newPlayers = state.play.initial.filter((plr) => plr.pieceId !== removed.id);
            const removedPlayers = state.play.initial.filter((plr) => plr.pieceId === removed.id);

            state.play.initial = newPlayers;

            const affectedBalls = state.play.balls.filter((ball) =>
                removedPlayers.some((plr) => plr.id === ball.carrier)
            );

            affectedBalls.forEach((ball) => removeBallMovement(state.play, ball.id));
            removedPlayers.forEach((plr) => removePlayerFromFrames(state.play.frames, plr));
            removeConceptsForPlayerIds(
                state.play,
                removedPlayers.map((plr) => plr.id)
            );
        }

        if (removed.type === ToolTypes.GOAL) {
            state.play.frames.forEach((frame) => {
                if (frame.shoot === removed.id) {
                    delete frame.shoot;
                }
            });
        }

        if ([ToolTypes.ELLIPSE, ToolTypes.RECTANGLE].includes(removed.type) && typeof removed.moveId !== 'undefined') {
            const currentFrame = state.play.frames[removed.frameIdx ?? state.currentFrame];
            const move = currentFrame.moves.find(({ target }) => removed.moveId === target);
            move.type = ToolTypes.MOVE;
        }
    },

    UpdatePlayerRotation: (state, action: PayloadAction<any>) => {
        const currentFrame = state.play.frames[state.currentFrame];

        // Initialize playerRotations if it doesn't exist yet
        currentFrame.playerRotations = currentFrame.playerRotations ?? [];

        const existingIdx = currentFrame.playerRotations.findIndex(
            (rotation: any) => rotation.target === action.payload.id
        );

        if (action.payload.rotation !== undefined) {
            if (existingIdx === -1) {
                currentFrame.playerRotations.push({
                    target: action.payload.id,
                    rotation: action.payload.rotation,
                });
            } else {
                currentFrame.playerRotations[existingIdx].rotation = action.payload.rotation;
            }
        } else if (existingIdx !== -1) {
            currentFrame.playerRotations.splice(existingIdx, 1);
        }
    },

    RemoveBallMovement: (state, action: PayloadAction<number>) => {
        removeBallMovement(state.play, action.payload);
    },

    MoveInitialBall: (state, action: PayloadAction<any>) => {
        const { PlayTypes } = state.PlayConfig;

        if (state.play.type === PlayTypes.DRILL) {
            updateExistingOrCreateNew(state.play.balls, {
                carrier: action.payload,
            });
        } else {
            state.play.balls = [
                {
                    id: 0,
                    carrier: action.payload,
                },
            ];
        }
    },

    TagPlayFormation: (
        state,
        action: PayloadAction<{ id?: string | null; name: string; attributes?: string[] }>
    ) => {
        tagPlayWithFormation(state.play, action.payload);
    },

    ApplyFormation: (state, action: PayloadAction<any>) => {
        const { initial, balls, fieldPieces, id, anchorType, name, attributes } = action.payload;

        const { pieces, goalies } = createDefaultGoals(state.PlayConfig, state.play);
        const anchor = getFormationAnchor(state.PlayConfig, state.play, anchorType, pieces);

        tagPlayWithFormation(state.play, { id, name, attributes });

        state.play.initial = applyFormationInitial(
            state.PlayConfig,
            state.play,
            { initial, anchorType },
            pieces,
            anchor
        );

        if (state.PlayConfig.Formations[id]) {
            state.play.fieldPieces = pieces;
            state.play.initial = state.play.initial.concat(
                goalies.map((goalie, idx) => ({ ...goalie, id: idx + state.play.initial.length }))
            );
        } else {
            state.play.fieldPieces = fieldPieces.map((piece) => ({
                ...piece,
                origin: applyOffset(anchor, piece.origin),
            }));
        }

        state.play.balls = balls;

        state.play.frames = state.play.frames.map(() => getEmptyFrame());

        if (state.PlayConfig.Site === Sites.footballlab) {
            ensureFootballPlayDefaults(state.PlayConfig, state.play);
        }
    },

    AddPlayConcept: (
        state,
        action: PayloadAction<{
            conceptId: string;
            playerIds: number[];
            name: string;
            description: string;
            tags?: string[];
        }>
    ) => {
        const { conceptId, playerIds, name, description, tags } = action.payload;

        if (!state.play.concepts) {
            state.play.concepts = [];
        }

        const isFirstInstance = !state.play.concepts.some((concept) => concept.conceptId === conceptId);
        const playConcept = {
            id: generatePlayConceptId(),
            conceptId,
            playerIds,
            startFrame: 0,
            endFrame: state.play.frames.length - 1,
            name,
            description,
            tags,
        };

        state.play.concepts.push(playConcept);

        if (isFirstInstance) {
            state.play.tags = [...(state.play.tags ?? []), name];
        }

        state.play.viewOptions = {
            ...state.play.viewOptions,
            spotlightPlayerIds: [...playerIds],
            activeConceptId: playConcept.id,
        };
    },

    ActivatePlayConcept: (state, action: PayloadAction<PlayConcept>) => {
        const concept = action.payload;

        state.play.viewOptions = {
            ...state.play.viewOptions,
            spotlightPlayerIds: [...concept.playerIds],
            activeConceptId: concept.id,
        };
    },

    RemovePlayConcept: (state, action: PayloadAction<{ id: string; conceptId: string; name?: string }>) => {
        const { id, conceptId, name } = action.payload;

        if (!state.play.concepts?.length) {
            return;
        }

        state.play.concepts = state.play.concepts.filter((concept) => concept.id !== id);

        const stillHasConcept = state.play.concepts.some((concept) => concept.conceptId === conceptId);

        if (name && !stillHasConcept) {
            state.play.tags = (state.play.tags ?? []).filter((tag) => tag !== name);
        }

        if (state.play.viewOptions?.activeConceptId === id) {
            state.play.viewOptions = {
                ...state.play.viewOptions,
                activeConceptId: null,
                spotlightPlayerIds: [],
            };
        }
    },

    ClearField: (state, action: PayloadAction<any>) => {
        const { FieldTypes } = state.PlayConfig;

        state.play.initial = [];
        state.play.fieldPieces = [];
        state.play.frames = state.play.frames.map(() => getEmptyFrame());
        state.play.balls = [];

        if (state.play.fieldType !== FieldTypes.CUSTOM) {
            const { pieces, goalies } = createDefaultGoals(state.PlayConfig, state.play);
            state.play.fieldPieces = pieces;
            state.play.initial = goalies;
        }
    },

    UpdateFrameTheme: (state, action: PayloadAction<any>) => {
        const currentFrame = state.play.frames[state.currentFrame];

        if (!currentFrame) return;

        currentFrame.theme = currentFrame.theme ?? [];

        const { theme } = currentFrame;

        const existingIdx = theme.findIndex((sl: any) => sl.target === action.payload.target);

        if (action.payload.color || action.payload.shape) {
            if (existingIdx !== -1) {
                theme.splice(existingIdx, 1, {
                    ...theme[existingIdx],
                    ...action.payload,
                });
            } else {
                theme.push(action.payload);
            }
        } else if (existingIdx !== -1) {
            theme.splice(existingIdx, 1);
        }
    },

    RemoveFrame: (state, action: PayloadAction<number>) => {
        const frameIdx = action.payload;
        state.play.frames.splice(frameIdx, 1);

        state.currentFrame = Math.min(frameIdx, state.play.frames.length - 1);

        state.playModified = true;
    },
};

const { reducer: playEditorReducer, actions: playEditorActions } = createSlice({
    name: 'playEditor',
    initialState: playEditorDefaultState,
    reducers: {
        CreatePlay: (state, action: PayloadAction<any>) => {
            const play = getNewPlay(state.PlayConfig, action.payload);

            if (state.PlayConfig.Site === Sites.footballlab) {
                ensureFootballPlayDefaults(state.PlayConfig, play);
            }

            // Returning new state to ensure that no properties of a previous play carry over to the new play
            return {
                ...state,
                currentFrame: 0,
                editHistory: [play],
                play,
            };
        },

        SetupField: (state, action) => {
            state.currentFrame = 0;
            state.play.fieldSetup = action.payload;
        },

        SelectFrame: (state, action) => {
            // Use frames.length to allow football scrubbing to the end of last frame
            const frameCount = state.play?.frames ? state.play.frames.length - 0.000001 : 0;
            state.currentFrame = Math.min(action.payload, frameCount);
        },

        SaveComment: (state, action) => {
            const { id, text } = action.payload;
            const currentFrame = state.play.frames[state.currentFrame];
            if (id === -1) {
                currentFrame.comment = text;
            } else {
                const fieldComment = currentFrame.fieldComments?.find((comm: any) => comm.id === id);

                if (fieldComment) {
                    fieldComment.text = text;
                } else {
                    throw new Error(
                        `Invalid save comment frameIdx: ${state.currentFrame} commentId: ${id} frameComments: ${currentFrame.fieldComments?.length}`
                    );
                }
            }

            state.playModified = true;
        },

        PlayLoaded: (state, action: PayloadAction<PlayData | null>) => {
            state.currentFrame = 0;
            state.play = undefined;
            state.playModified = false;

            if (action.payload) {
                const conformed = conformPlayData(state.PlayConfig, action.payload);
                state.play = conformed;
                state.playModified = JSON.stringify(conformed) !== JSON.stringify(action.payload);
                state.editHistory = [state.play];
            } else {
                state.editHistory = [];
            }
        },

        PlayModified: (state, action) => {
            state.playModified = action.payload;
        },

        UpdateFieldType: {
            prepare: (type: string, width?: number, height?: number) => ({
                payload: { type, width, height },
            }),
            reducer: (state, action: PayloadAction<any>) => {
                const { type, width, height } = action.payload;
                const { FieldTypes } = state.PlayConfig;

                state.play.fieldType = type;
                state.play.fieldConfig =
                    type === FieldTypes.CUSTOM
                        ? {
                              width,
                              height,
                          }
                        : null;

                if (type !== FieldTypes.CUSTOM) {
                    const { pieces, goalies } = createDefaultGoals(state.PlayConfig, state.play);
                    state.play.fieldPieces = pieces;
                    state.play.initial = goalies;
                }

                state.playModified = true;
            },
        },

        Saving: (state, action) => {
            state.savingPlay = action.payload;
        },

        NewPlaySaved: (state, action) => {
            if (state.play) {
                state.play.id = action.payload;
                state.play.lastUpdated = new Date();
                // Record that the play is new to avoid unexpected behavior for a play that is not yet reflected in my playbook
                state.play.new = true;
            }
        },

        Undo: (state) => {
            if (state.editHistory.length > 1) {
                state.editHistory.shift();
                [state.play] = state.editHistory;
                state.playModified = true;
                state.currentFrame = Math.min(state.play.frames.length - 1, state.currentFrame);
            }
        },

        UpdatePlay: (state, action) => {
            Object.assign(state.play, action.payload);
            state.playModified = true;
        },

        UpdateMarkers: (state, action: PayloadAction<Point[]>) => {
            if (action.payload) {
                state.debugMarkers = [...(state.debugMarkers ?? []), ...action.payload];
            } else {
                state.debugMarkers = [];
            }
        },

        UpdateDebugOptions: (state, action: PayloadAction<object | null>) => {
            if (action.payload) {
                state.debugOptions = {
                    ...(state.debugOptions ?? {}),
                    ...action.payload,
                };
            } else {
                state.debugOptions = null;
            }
        },

        ...playModifierReducers,
    },
    extraReducers: (builder) =>
        builder
            .addCase(authActions.PurgeProfile, (state) => ({
                ...playEditorDefaultState,
                PlayConfig: state.PlayConfig,
            }))
            .addMatcher(
                (action): action is PayloadAction =>
                    Boolean(
                        playModifierReducers[
                            action.type.replace('playEditor/', '') as keyof typeof playModifierReducers
                        ]
                    ),
                (state) => {
                    state.playModified = true;

                    state.editHistory.unshift(state.play);

                    state.editHistory = state.editHistory.slice(0, 100);
                }
            ),
});

export { playEditorReducer, playEditorActions, playEditorDefaultState };
